import { Component } from '@theme/component';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} BundlePickerRefs
 * @property {HTMLInputElement[]} inputs - The pack radio inputs.
 */

/**
 * A custom element that lets a shopper pick between the page product and separate bundle
 * products (e.g. 1 bottle / 3-pack / 5-pack) that live as their own SKUs in Shopify.
 *
 * Unlike `<variant-picker>`, nothing here is a variant of the page product. Selecting a card
 * re-renders the main product section with the *bundle* product as context (Section Rendering
 * API) and dispatches the theme's own `ProductSelectEvent`, so `product-form-component`,
 * `product-price` and `sticky-add-to-cart` update themselves exactly as they would for a
 * variant change: the hidden `input[name="id"]` becomes the bundle's variant, the big price
 * flips to the bundle price, and Add to cart adds the real bundle SKU.
 *
 * @extends Component<BundlePickerRefs>
 */
export default class BundlePicker extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.packChanged);

    // If no card matches the form's current variant (e.g. pack 1 was pointed at another
    // product in the editor), fall back to the first enabled card so the UI is never blank.
    const inputs = this.refs.inputs ?? [];
    if (!inputs.some((radio) => radio.checked)) {
      const first = inputs.find((radio) => radio.getAttribute('aria-disabled') !== 'true');
      if (first) {
        first.checked = true;
        first.dataset.currentChecked = 'true';
        this.#setFormVariantId(first.dataset.variantId ?? '');
      }
    }

    this.#selectPackFromUrl();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.packChanged);
    this.#abortController?.abort();
  }

  /**
   * Handles a pack card being selected.
   * @param {Event} event
   */
  packChanged = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (input.getAttribute('aria-disabled') === 'true') {
      this.#restoreChecked();
      return;
    }

    for (const radio of this.refs.inputs ?? []) {
      radio.dataset.currentChecked = radio === input ? 'true' : 'false';
    }

    this.#selectPack(input);
  };

  /**
   * Preselects the pack named by `?pack=<bottles in pack>`, so emails can link straight to
   * e.g. the 5-pack.
   */
  #selectPackFromUrl() {
    const pack = new URLSearchParams(window.location.search).get('pack');
    if (!pack) return;

    const inputs = this.refs.inputs ?? [];
    const input = inputs.find(
      (radio) => radio.dataset.packQuantity === pack && radio.getAttribute('aria-disabled') !== 'true'
    );
    if (!input || input.checked) return;

    input.checked = true;
    for (const radio of inputs) {
      radio.dataset.currentChecked = radio === input ? 'true' : 'false';
    }
    this.#setFormVariantId(input.dataset.variantId ?? '');

    // The form, price and sticky bar subscribe to ProductSelectEvent when they connect, and
    // their modules load after this one, so wait for them before announcing the change.
    const listeners = ['product-form-component', 'product-price', 'sticky-add-to-cart'].filter((tag) =>
      this.closest('.shopify-section')?.querySelector(tag)
    );
    Promise.all(listeners.map((tag) => customElements.whenDefined(tag))).then(() => {
      if (input.checked) this.#selectPack(input);
    });
  }

  /**
   * Re-check whichever card was current before an invalid click.
   */
  #restoreChecked() {
    const current = (this.refs.inputs ?? []).find((radio) => radio.dataset.currentChecked === 'true');
    if (current) current.checked = true;
  }

  /**
   * Re-renders the section with the chosen pack's product and notifies the theme.
   * @param {HTMLInputElement} input
   */
  #selectPack(input) {
    const { productUrl = '', variantId = '', packLabel = '' } = input.dataset;
    const sectionId = this.dataset.sectionId;
    const pageProductId = this.dataset.productId ?? '';
    const resource = this.#parseVariant(input);

    // Belt and braces: set the form's variant id immediately so a fast Add to cart click can
    // never submit the wrong SKU, even if the section fetch below is slow or fails.
    this.#setFormVariantId(variantId);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const deferred = ProductSelectEvent.createPromise();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: pageProductId,
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions: [{ name: this.dataset.optionName ?? 'Pack', value: packLabel }],
        detail: { optionValueId: variantId, variantId, connectedProductUrl: '' },
        promise: deferred.promise,
      })
    );

    if (!sectionId || !productUrl) {
      deferred.resolve({ variant: this.#toStandardVariant(resource, packLabel), detail: { html: null } });
      return;
    }

    const url = `${productUrl.split('?')[0]}?sections=${encodeURIComponent(sectionId)}`;

    // No `Accept: application/json` here: with that header Shopify serves /products/<handle> as
    // the Product JSON endpoint and ignores `?sections=`, returning {"product": ...} instead.
    fetch(url, { signal })
      .then((response) => response.json())
      .then((sections) => {
        const markup = sections?.[sectionId];
        if (typeof markup !== 'string') throw new Error(`Section ${sectionId} missing from response`);

        const html = new DOMParser().parseFromString(markup, 'text/html');
        this.#applySalePrice(input, html);

        // Keep the picker's own selection: the fetched HTML was rendered for the bundle product,
        // which does not own this block's state, and listeners only read what they need from it.
        deferred.resolve({
          variant: this.#toStandardVariant(resource, packLabel),
          detail: {
            html,
            // The page product id is passed on purpose: product-form, product-price and
            // sticky-add-to-cart all ignore updates whose productId differs from their own.
            productId: pageProductId,
            sourceId: variantId,
            resource,
          },
        });

        // The hidden input is re-set from `resource.id` by product-form; keep our own copy in
        // sync in case that handler bailed for any reason.
        this.#setFormVariantId(variantId);
      })
      .catch((error) => {
        deferred.reject(error);
        if (error?.name !== 'AbortError') console.error('[bundle-picker]', error);
      });
  }

  /**
   * Puts a pack's sale price into the re-rendered price blocks and sticky bar, which were
   * rendered for the pack product and only know its regular price.
   * @param {HTMLInputElement} input
   * @param {Document} html
   */
  #applySalePrice(input, html) {
    const template = input.closest('label')?.querySelector('template[data-sale-price]');
    if (!(template instanceof HTMLTemplateElement)) return;

    for (const container of html.querySelectorAll(
      'product-price [ref="priceContainer"], .sticky-add-to-cart__price [ref="priceContainer"]'
    )) {
      container.replaceChildren(template.content.cloneNode(true));
    }
  }

  /**
   * Writes the chosen variant id straight into the product form in this section.
   * @param {string} variantId
   */
  #setFormVariantId(variantId) {
    if (!variantId) return;
    const section = this.closest('.shopify-section');
    const input = section?.querySelector('product-form-component input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = variantId;
  }

  /**
   * Reads the embedded variant JSON for a card.
   * @param {HTMLInputElement} input
   * @returns {any}
   */
  #parseVariant(input) {
    const script = input.closest('label')?.querySelector('script[type="application/json"]');
    try {
      return script?.textContent ? JSON.parse(script.textContent) : null;
    } catch {
      return null;
    }
  }

  /**
   * Shapes a Liquid variant object into the standard-events variant payload.
   * @param {any} variant
   * @param {string} packLabel
   */
  #toStandardVariant(variant, packLabel) {
    if (!variant) return null;
    return {
      id: variant.id,
      title: variant.title ?? packLabel,
      availableForSale: Boolean(variant.available),
      price: {
        amount: (Number(variant.price ?? 0) / 100).toFixed(2),
        currencyCode: this.dataset.currency ?? '',
      },
      selectedOptions: [{ name: this.dataset.optionName ?? 'Pack', value: packLabel }],
    };
  }
}

if (!customElements.get('bundle-picker')) {
  customElements.define('bundle-picker', BundlePicker);
}
